/* eslint-disable no-unreachable */
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  concat,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import {
  Authenticator,
  Heading,
  Image,
  Text,
  useTheme,
  View,
  withAuthenticator,
} from "@aws-amplify/ui-react";
import { onError } from "apollo-link-error";
import { Auth } from "aws-amplify";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import QRCode from "qrcode"; // Import QRCode library
import DialogHoc from "../components/hoc/Dialog/dialogHOC";
import Layout from "../components/layout";
import { isPlatformAdmin } from "../hooks/utils";
import { COPY_RIGHTS, GRAPHQL_URL, SMAI_LOGO } from "../config";
import { GET_REFRESH_TOKEN } from "../graphql/mutation";
import resolvers from "../graphql/resolvers";
import typeDefs from "../graphql/typeDefs";
import { colorConfig } from "../library/colorConfig";
import { getLocationId } from "../redux/slices/locationSlice";
import {
  ERROR_BOUNDARY_REDIRECT_PATH,
  getLocalStorage,
  removeLocalStorage,
} from "../utils/storages";

const utc = require("dayjs/plugin/utc");
// import utc from 'dayjs/plugin/utc' // ES 2015

const timezone = require("dayjs/plugin/timezone");

dayjs.extend(utc);
dayjs.extend(timezone);
const timeZone = dayjs.tz.guess(); // America/Chicago

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    );
  if (networkError) {
    console.log(`[NetworkError error]: Message: ${networkError.message}`);
  }
});

const getJwtToken = async () => {
  return new Promise(resolve => {
    const storedToken = sessionStorage.getItem("token");
    const exp = sessionStorage.getItem("exp");
    let isExpired = false;
    if (exp) {
      const expiredDate = new Date(parseInt(exp));
      isExpired = expiredDate <= new Date();
    }
    if (!storedToken || !exp || isExpired) {
      Auth.currentSession()
        .then(session => session.getAccessToken())
        .then(accessToken => {
          if (accessToken && accessToken !== null) {
            const jwtToken = accessToken.getJwtToken();
            sessionStorage.setItem("token", jwtToken);
            sessionStorage.setItem("exp", (accessToken.payload.exp * 1000).toString());
            resolve(jwtToken);
          }
        });
    } else {
      resolve(storedToken);
    }
  });
};

const refreshToken = () => {
  return new Promise((resolve, reject) => {
    (async () => {
      const storedToken = sessionStorage.getItem("token");
      const exp = sessionStorage.getItem("exp");
      let almostExpired = false;
      if (exp) {
        const expiredDate = new Date(parseInt(exp) - 15 * 60 * 1000);
        almostExpired = expiredDate <= new Date();
      }
      if (!storedToken || !exp || almostExpired) {
        try {
          const cognitoUser = await Auth.currentAuthenticatedUser();
          const currentSession = await Auth.currentSession();
          cognitoUser.refreshSession(currentSession.refreshToken, (err, session) => {
            const { accessToken } = session;
            const jwtToken = accessToken.getJwtToken();
            sessionStorage.setItem("token", jwtToken);
            sessionStorage.setItem("exp", (accessToken.payload.exp * 1000).toString());
            resolve(jwtToken);
          });
        } catch (e) {
          console.log("Unable to refresh token", e);
          reject();
        }
      } else {
        resolve(storedToken);
      }
    })();
  });
};

export const UserContext = React.createContext();

const AuthenticatorProvider = props => {
  const { persistor, children, authData, authState } = props;

  const locationIdslice = useSelector(state => getLocationId(state));

  const [client, setClient] = useState(null);
  const [token, setToken] = useState(null);
  const [auth, setAuth] = useState(null);
  const [modal, setmodal] = useState(false);
  const [fullScreen, setMaximizeScreen] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [loadingAuth, setloadingAuth] = useState(false);
  const [addnewLead, setAddNewLead] = useState(false);
  const [newLead, setNewLead] = useState(null);
  const [socketMsgSelectedLead, setSocketMsgSelectedLead] = useState("");
  const [socket, setSocket] = useState(null);
  const [refetchAppointments, setrefetchAppointments] = useState(false);
  const [isUserIsBeingSet, setIsUserIsBeingSet] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(true);
  const [user, setUser] = useState({
    email: "",
    fullName: "",
    phone: "",
    isDisabled: false,
    locationId: "",
    company: {
      name: "",
      isDisabled: false,
      aingineSourceId: null,
      isOptinConsentMethod: false,
      crmIntegrationType: "",
    },
    role: {
      name: "default",
      canCreateTeams: false,
      canCreateUsers: false,
      canViewProspects: false,
    },
    teamsLeader: null,
    accessControlPermissions: [],
  });

  const [mfaStep, setMfaStep] = useState(false);
  const [isFirstTimeMFA, setIsFirstTimeMFA] = useState(false);
  const [cognitoAuthUser, setCognitoAuthUser] = useState(null);
  const [qrCodeImage, setQrCodeImage] = useState(null); // State to store QR code image
  const [showQrCode, setShowQrCode] = useState(false); // State to control QR code visibility
  const [verificationCode, setVerificationCode] = useState(""); // State for user input

  const handleFirstTimeMFASetup = async cognitoUser => {
    try {
      console.log("============== cognitoUser ===========", cognitoUser);
      const mfaEnabled = cognitoUser.preferredMFA || "NOMFA"; // Use preferredMFA from cognitoUser
      console.log("handleFirstTimeMFASetup >>>>>> Preferred MFA:", mfaEnabled);

      if (mfaEnabled === "SOFTWARE_TOKEN_MFA") {
        console.log("MFA is already active with Authenticator App.");
        setAuth(cognitoUser);
        setloadingAuth(true);
      } else if (mfaEnabled === "NOMFA") {
        console.log("Setting up TOTP for MFA...");
        const totpCode = await Auth.setupTOTP(cognitoUser); // Generate TOTP secret
        // const totpCode = "KKYTKK7ZJUPONVPIXM2DCXESLBA4GIAJBIUB7SJNSLWQF6I4VEKQ";
        console.log("TOTP Code:", totpCode);

        // Generate QR code URL
        const qrCodeUrl = `otpauth://totp/${
          cognitoUser?.attributes?.email || cognitoUser.username
        }?secret=${totpCode}&issuer=EngagedAi`;
        const qrCodeImage = await QRCode.toDataURL(qrCodeUrl);
        setQrCodeImage(qrCodeImage);
        setShowQrCode(true); // Show the QR code
      } else {
        console.warn("Unexpected MFA type:", mfaEnabled);
        setIsFirstTimeMFA(true);
      }
    } catch (error) {
      console.error("Error during MFA setup:", error);
      if (error?.message?.includes("User has not verified software token mfa")) {
        console.log("User has not verified TOTP. Prompting for verification...");
        setShowQrCode(true); // Show QR code if verification is pending
      }
    }
  };

  const handleVerifyCode = async cognitoUser => {
    try {
      await Auth.verifyTotpToken(cognitoUser, verificationCode); // Verify the TOTP token
      await Auth.setPreferredMFA(cognitoUser, "SOFTWARE_TOKEN_MFA"); // Enable software token MFA
      console.log("TOTP setup verified and MFA enabled.");
      setIsFirstTimeMFA(true);
      setShowQrCode(false); // Hide QR code after verification
      setAuth(cognitoUser);
      setloadingAuth(true);
    } catch (error) {
      console.error("Error verifying TOTP token:", error);
      if (error?.code === "EnableSoftwareTokenMFAException") {
        console.log("Software token already used. Enabling MFA explicitly...");
        try {
          await Auth.setPreferredMFA(cognitoUser, "SOFTWARE_TOKEN_MFA"); // Explicitly enable MFA
          console.log("MFA enabled successfully.");
          setIsFirstTimeMFA(true);
          setShowQrCode(false); // Hide QR code after enabling MFA
        } catch (enableError) {
          console.error("Error enabling software token MFA:", enableError);
          alert("Failed to enable MFA. Please try again.");
        }
      } else {
        alert("Invalid verification code. Please try again.");
      }
    }
  };

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then(async cognitoUser => {
        console.log("============== cognitoUser ===========", cognitoUser);
        const mfaVerified = sessionStorage.getItem("mfaCodeVerified");
        const isAdminUser = isPlatformAdmin(cognitoUser);
        console.log("MFA mfaVerified.", mfaVerified, cognitoUser.preferredMFA, isAdminUser);

        const cognitoUserMFASetting = cognitoUser?.attributes?.locale
          ? JSON.parse(cognitoUser?.attributes?.locale || "")
          : {};
        console.log("============== cognitoUserMFASetting ===========", cognitoUserMFASetting);
        if (
          !mfaVerified &&
          cognitoUser.preferredMFA === "NOMFA" &&
          !isAdminUser &&
          cognitoUserMFASetting?.mfa_enabled
        ) {
          console.log("MFA not verified. Proceeding to MFA setup.");
          handleFirstTimeMFASetup(cognitoUser);
          setCognitoAuthUser(cognitoUser);
        } else {
          console.log("MFA already verified. Proceeding to authenticated state.");
          setAuth(cognitoUser);
          setloadingAuth(true);
        }
        // Check for redirect path after successful authentication
        const redirectPath = getLocalStorage(ERROR_BOUNDARY_REDIRECT_PATH);
        if (redirectPath) {
          window.location.href = redirectPath;
          removeLocalStorage(ERROR_BOUNDARY_REDIRECT_PATH);
        }
      })
      .catch(error => {
        console.error("Error fetching authenticated user:", error);
      });
  }, []);

  useEffect(() => {
    if (auth) {
      console.log("MFA verified. Proceeding to authenticated state >> Home.");
    }
  }, [auth]);

  const updateUser = _loginUser => {
    let loginUser = { ..._loginUser };
    loginUser.token = token;

    const allActiveUserAccounts = loginUser.userAccounts.filter(res => {
      if (!res.company.isDisabled && !res.isDisabled) {
        return true;
      }
    });

    const allUserAccounts = allActiveUserAccounts.map(res => {
      return res.company;
    });

    let userRoleObj = loginUser.role;
    if (loginUser?.company?.userRoles?.length > 0) {
      const found = loginUser.company.userRoles.find(x => x.userId === Number(loginUser.id));
      userRoleObj = found ? found.role : userRoleObj;
    }

    const getActiveCrmIntegration = company => {
      let type = "";
      let { locationId } = loginUser;
      if (sessionStorage?.getItem("currentLocationId")?.includes(",")) {
        //
      } else {
        locationId = sessionStorage.getItem("currentLocationId");
      }
      if ((company?.crmIntegration || []).length > 0) {
        const findIndex = (company?.crmIntegration).findIndex(
          el => el.active && +el.locationId === +locationId
        );
        if (findIndex > -1) {
          type = company?.crmIntegration[findIndex].integrationType;
        }
      }
      return type;
    };

    const userTemp = {
      ...loginUser,
      id: loginUser.id,
      email: loginUser.email,
      fullName: loginUser.fullName,
      phone: loginUser.phone,
      firstName: loginUser.firstName,
      lastName: loginUser.lastName,
      isDisabled: loginUser.isDisabled,
      locationId: loginUser.locationId,
      company: {
        id: loginUser.company.id,
        name: loginUser.company.name,
        isDisabled: loginUser.company.isDisabled,
        aingineSourceId: loginUser.company.aingineSourceId,
        timezone: loginUser.company.timezone,
        isOptinConsentMethod: loginUser.company.isOptinConsentMethod,
        crmIntegrationType: getActiveCrmIntegration(loginUser?.company),
        isOpenCrmWarningPopup: loginUser.company.isOpenCrmWarningPopup,
      },
      role: userRoleObj,
      teamsLeader: loginUser.teamsLeader,
      userAccounts: allUserAccounts.sort((a, b) => (a.name > b.name ? 1 : -1)),
      accessControlPermissions: user.accessControlPermissions,
      companyRole: loginUser?.companyRole || {},
      location: loginUser?.location,
      locations: loginUser?.locations,
    };
    loginUser = userTemp;
    setIsUserIsBeingSet(false);
    setUser(loginUser);
  };

  const updateUserWithPermissions = (_loginUser, permissions) => {
    let loginUser = { ..._loginUser };
    loginUser.token = token;

    const allActiveUserAccounts = (loginUser.userAccounts || []).filter(res => {
      if (!res.company.isDisabled && !res.isDisabled) {
        return true;
      }
    });

    const allUserAccounts = allActiveUserAccounts.map(res => {
      return res.company;
    });

    let userRoleObj = loginUser.role;
    if (loginUser?.company?.userRoles?.length > 0) {
      const found = loginUser.company.userRoles.find(x => x.userId === Number(loginUser.id));
      userRoleObj = found ? found.role : userRoleObj;
    }

    const getActiveCrmIntegration = company => {
      let type = "";
      let { locationId } = loginUser;
      if (sessionStorage?.getItem("currentLocationId")?.includes(",")) {
        //
      } else {
        locationId = sessionStorage.getItem("currentLocationId");
      }
      if ((company?.crmIntegration || []).length > 0) {
        const findIndex = (company?.crmIntegration).findIndex(
          el => el.active && +el.locationId === +locationId
        );
        if (findIndex > -1) {
          type = company?.crmIntegration[findIndex].integrationType;
        }
      }
      return type;
    };

    const userTemp = {
      ...loginUser,
      id: loginUser.id,
      email: loginUser.email,
      fullName: loginUser.fullName,
      phone: loginUser.phone,
      firstName: loginUser.firstName,
      lastName: loginUser.lastName,
      isDisabled: loginUser.isDisabled,
      locationId: loginUser.locationId,
      company:
        {
          ...loginUser?.company,
          crmIntegrationType: getActiveCrmIntegration(loginUser?.company),
        } || {},
      role: userRoleObj,
      teamsLeader: loginUser.teamsLeader,
      userAccounts: allUserAccounts.sort((a, b) => (a.name > b.name ? 1 : -1)),
      accessControlPermissions: permissions,
      companyRole: loginUser?.companyRole || {},
      location: loginUser?.location,
      locations: loginUser?.locations,
    };
    loginUser = userTemp;
    setIsUserIsBeingSet(false);
    setUser(loginUser);
  };

  const httpLink = new HttpLink({ uri: GRAPHQL_URL });

  const authMiddleware = new ApolloLink((operation, forward) => {
    // add the authorization to the headers
    const sesstionToken = sessionStorage.getItem("token");
    const tokenInfo = sesstionToken || token;

    let customHeader = {
      authorization: tokenInfo ? `Bearer ${tokenInfo}` : "",
      refreshToken: sessionStorage.getItem("refreshToken"),
    };
    if (locationIdslice != null || sessionStorage.getItem("currentLocationId") !== null) {
      customHeader = {
        ...customHeader,
        sLocationId:
          locationIdslice === "all"
            ? sessionStorage.getItem("currentLocationId")
            : locationIdslice !== null
            ? locationIdslice
            : sessionStorage.getItem("currentLocationId"),
        "X-Timezone": timeZone,
      };
    }
    // eslint-disable-next-line no-unused-vars
    operation.setContext(({ headers = {} }) => ({
      headers: { ...customHeader },
    }));
    // let isTokenRefreshing = false;
    return forward(operation);
    return forward(operation).map(response => {
      if (
        (response &&
          response.errors &&
          response.errors.length > 0 &&
          response.errors[0].message &&
          response.errors[0].message.includes("401")) ||
        (response &&
          response.errors &&
          response.errors.length > 0 &&
          response.errors[0].message &&
          response.errors[0].message.includes("Signature"))
      ) {
        const refreshToken = sessionStorage.getItem("refreshToken");
        client &&
          client
            ?.mutate({
              mutation: GET_REFRESH_TOKEN,
              variables: { refreshToken },
            })
            .then(res => {
              sessionStorage.setItem("token", res.data.generateAccessToken.accessToken);
              setToken(res.data.generateAccessToken.accessToken);
            })
            .finally(() => {});
      }
      return response;
    });
  });

  const getRefreshToken = async () => {
    const currentSession = await Auth.currentSession();
    sessionStorage.setItem("refreshToken", currentSession.getRefreshToken().token);
  };

  useEffect(() => {
    sessionStorage.setItem("isLogin", true);
    getRefreshToken();
  }, []);

  useEffect(() => {
    console.log("MFA verified. Proceeding to authenticated state >> Home.");
    if (sessionStorage.getItem("isLogin")) {
      getJwtToken().then(t => {
        setToken(t);
      });
      const links = ApolloLink.from([errorLink, httpLink]);
      setClient(
        new ApolloClient({
          cache: new InMemoryCache({
            addTypename: false,
          }),
          link: concat(authMiddleware, links),
          resolvers,
          typeDefs,
        })
      );

      const refreshInterval = setInterval(() => {
        refreshToken().then(newToken => {
          Auth.currentSession().then(() => {
            Auth.currentAuthenticatedUser().then(() => {});
          });
        });
      }, 5 * 60 * 1000);

      return () => clearInterval(refreshInterval);
    }
  }, [token, locationIdslice]);

  function deleteAllCookies() {
    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }

  if (showQrCode) {
    const { tokens } = useTheme();
    return (
      <div
        className="min-h-screen qrcode_page"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <View textAlign="center" padding={tokens.space.large}>
          <Image alt="smai logo" src={SMAI_LOGO} className="qrcode_logo w-[80%]" />
        </View>
        <View textAlign="center" padding="medium" className="qrcode_box w-[25%]">
          <Heading
            color={colorConfig.WHITE}
            padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
            level={3}
          >
            Scan the QR Code
          </Heading>
          <Text
            color={colorConfig.WHITE}
            padding={`0 ${tokens.space.xl} ${tokens.space.small} ${tokens.space.xl}`}
            style={{ fontSize: "14px" }}
          >
            Use your authenticator app to scan the QR code below or manually enter the secret.
          </Text>
          {qrCodeImage && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Image
                src={qrCodeImage}
                alt="QR Code"
                style={{
                  margin: "20px 0",
                  border: "1px solid #ccc",
                  borderRadius: "5px",
                  padding: "10px",
                  backgroundColor: "#fff",
                }}
              />
              <a
                href={qrCodeImage}
                download="enagedai-qr-code.png"
                style={{
                  color: "#047d95",
                  textDecoration: "none",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Download QR Code
              </a>
            </div>
          )}
          <View padding={`0 ${tokens.space.xl} 0 ${tokens.space.xl}`}>
            <input
              type="text"
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
              placeholder="Enter verification code"
              style={{
                width: "100%",
                padding: "10px",
                marginTop: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                fontSize: "14px",
              }}
            />
            <button
              type="button"
              onClick={() => handleVerifyCode(cognitoAuthUser)}
              className="amplify-button--primary w-full p-3 mt-4 rounded-md"
            >
              Verify
            </button>
          </View>
          <View textAlign="center" padding={tokens.space.medium}>
            <a
              href="/"
              style={{
                color: "#047d95",
                textDecoration: "none",
                fontSize: "14px",
                cursor: "pointer",
              }}
              onClick={() => {
                Auth.signOut().then(() => {
                  setQrCodeImage(null);
                  setShowQrCode(false);
                  sessionStorage.clear();
                  localStorage.clear();
                  deleteAllCookies();
                });
              }}
            >
              Back to Sign In
            </a>
          </View>
        </View>
        <View textAlign="center" padding={tokens.space.large} className="w-[25%]">
          <Text
            color={{
              base: colorConfig.WHITE,
            }}
          >
            {COPY_RIGHTS}
          </Text>
        </View>
      </div>
    );
  }

  const authContent = auth || authData;

  return client && loadingAuth ? (
    <ApolloProvider client={client}>
      <UserContext.Provider
        value={{
          user: user,
          fullScreen,
          setMaximizeScreen,
          notificationData,
          socket,
          setSocket,
          setNotificationData,
          setUser,
          authData: authContent,
          socketMsgSelectedLead,
          setSocketMsgSelectedLead,
          modal: modal,
          setmodal: setmodal,
          isUserIsBeingSet,
          persistor: persistor,
          newLead,
          setNewLead,
          addnewLead,
          setAddNewLead,
          isFullScreen,
          setIsFullScreen,
          refetchAppointments,
          setrefetchAppointments,
        }}
      >
        <DialogHoc />
        <Layout
          authData={authContent}
          updateUser={updateUser}
          updateUserWithPermissions={updateUserWithPermissions}
        >
          {React.cloneElement(children, {
            authState: authState,
            authData: authContent,
          })}
        </Layout>
      </UserContext.Provider>
    </ApolloProvider>
  ) : null;
};

const components = {
  Header() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Image alt="smai logo" src={SMAI_LOGO} />
      </View>
    );
  },

  Footer() {
    const { tokens } = useTheme();

    return (
      <View textAlign="center" padding={tokens.space.large}>
        <Text
          color={{
            base: colorConfig.WHITE,
          }}
        >
          {COPY_RIGHTS}
        </Text>
      </View>
    );
  },

  SignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          color={colorConfig.WHITE}
          padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={3}
        >
          Sign in to your account
        </Heading>
      );
    },
    Footer() {
      const { tokens } = useTheme();
      return (
        <>
          <View padding={`0 ${tokens.space.xl} 0 ${tokens.space.xl}`} textAlign="center">
            <span style={{ color: "#fff", fontSize: 14 }}>
              By clicking sign-in, I agree to be subject to the{" "}
              <a
                href="https://engagedai.io/termsofservice"
                target="_blank"
                style={{
                  textDecoration: `none`,
                  color: "#047d95",
                }}
                rel="noreferrer"
              >
                Terms of Service
              </a>
              .
            </span>
          </View>
          <View textAlign="center" padding={`${tokens.space.small} 0 ${tokens.space.small} 0`}>
            <Authenticator.SignIn.Footer />
          </View>
        </>
      );
    },
  },

  ConfirmSignIn: {
    Header() {
      const { tokens } = useTheme();

      return (
        <Heading
          color={colorConfig.WHITE}
          // padding={`${tokens.space.xl} 0 0 ${tokens.space.xl}`}
          level={4}
          className="text-left"
        >
          Enter the verification code from your authenticator app
        </Heading>
      );
    },
    Footer() {
      const { tokens } = useTheme();

      return (
        <View padding={`0 ${tokens.space.xl} 0 ${tokens.space.xl}`} textAlign="center">
          <span style={{ color: "#fff", fontSize: 14 }}>
            By clicking sign-in, I agree to be subject to the{" "}
            <a
              href="https://engagedai.io/termsofservice"
              target="_blank"
              style={{
                textDecoration: `none`,
                color: "#047d95",
              }}
              rel="noreferrer"
            >
              Terms of Service
            </a>
            .
          </span>
        </View>
      );
    },
    // async onSuccess() {
    //   // Redirect to engagements/activity-center/ after successful sign-in
    //   window.location.href = "/engagements/activity-center/";
    // },
  },
};

export default withAuthenticator(AuthenticatorProvider, {
  components,
  hideSignUp: true,
});
